import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import {
  HUB_RECORD_LABELS,
  HUB_RECORD_TYPES,
  HubRecordInputSchema,
  type HubRecord,
  type HubRecordType,
} from "../../shared/tool-hubs";
import { Button } from "../components/ui/button";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { SheetBody, SheetFooter, SheetForm } from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { createToolHubRecord, uploadToolHubFile } from "./tool-hubs-api";

type Direction = "inbound" | "outbound" | "internal";
type Draft = {
  type: HubRecordType;
  title: string;
  occurredOn: string;
  bodyText: string;
  direction: Direction | "";
  followUpOn: string;
  replaces: string;
  file: File | null;
};

const localDate = (date = new Date()) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
const emptyDraft = (type: HubRecordType = "call"): Draft => ({
  type,
  title: "",
  occurredOn: localDate(),
  bodyText: "",
  direction: type === "note" || type === "decision" ? "" : "inbound",
  followUpOn: "",
  replaces: "",
  file: null,
});
/** Today's entries keep the current time so the timeline orders them; other days use noon. */
function occurredAt(day: string) {
  if (day === localDate()) return Math.floor(Date.now() / 1000);
  return Math.floor(new Date(`${day}T12:00:00`).getTime() / 1000);
}

export function HubComposer({
  organizationId,
  hubId,
  canUpload,
  decisions,
  onAdded,
}: {
  organizationId: string;
  hubId: string;
  canUpload: boolean;
  decisions: readonly HubRecord[];
  onAdded: () => void;
}) {
  const [draft, setDraft] = useState(emptyDraft);
  const save = useMutation({
    mutationFn: async () => {
      const artifactIds = draft.file
        ? [await uploadToolHubFile(organizationId, hubId, draft.file, draft.title)]
        : [];
      const input = HubRecordInputSchema.parse({
        type: draft.type,
        title: draft.title,
        occurredAt: occurredAt(draft.occurredOn),
        bodyText: draft.bodyText.trim() ? draft.bodyText : null,
        direction: draft.direction || null,
        followUpOn: draft.followUpOn || null,
        replaces: draft.type === "decision" && draft.replaces ? draft.replaces : null,
        artifactIds,
      });
      return createToolHubRecord(organizationId, hubId, input);
    },
    onSuccess: () => {
      setDraft(emptyDraft(draft.type));
      onAdded();
    },
  });
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };
  return (
    <SheetForm
      aria-label="Log to this tool"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <SheetBody>
        <fieldset disabled={save.isPending} className="space-y-4">
          <ComposerType
            draft={draft}
            onType={(type) => {
              setDraft((current) => ({
                ...emptyDraft(type),
                title: current.title,
                bodyText: current.bodyText,
              }));
            }}
          />
          <ComposerFields draft={draft} set={set} canUpload={canUpload} decisions={decisions} />
        </fieldset>
        {save.error ? <FieldError>{save.error.message}</FieldError> : null}
      </SheetBody>
      <SheetFooter
        start={
          <p className="m-0 max-w-64 text-sm text-muted-foreground">
            No patient or sample data. Your team and your AI agents read everything logged here.
          </p>
        }
      >
        <Button type="submit" disabled={save.isPending || !draft.title.trim()}>
          <Plus />{" "}
          {save.isPending ? "Adding…" : `Add ${HUB_RECORD_LABELS[draft.type].toLowerCase()}`}
        </Button>
      </SheetFooter>
    </SheetForm>
  );
}

function ComposerType({ draft, onType }: { draft: Draft; onType: (type: HubRecordType) => void }) {
  return (
    <div role="radiogroup" aria-label="Type" className="flex flex-wrap gap-2">
      {HUB_RECORD_TYPES.map((type) => (
        <Button
          key={type}
          type="button"
          role="radio"
          aria-checked={draft.type === type}
          variant={draft.type === type ? "secondary" : "ghost"}
          onClick={() => {
            onType(type);
          }}
        >
          {HUB_RECORD_LABELS[type]}
        </Button>
      ))}
    </div>
  );
}

function ComposerFields({
  draft,
  set,
  canUpload,
  decisions,
}: {
  draft: Draft;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  canUpload: boolean;
  decisions: readonly HubRecord[];
}) {
  const decision = draft.type === "decision";
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <Field>
          <FieldLabel htmlFor="hub-record-title">
            {decision ? "What was decided" : "Title"}
          </FieldLabel>
          <Input
            id="hub-record-title"
            value={draft.title}
            maxLength={240}
            placeholder={decision ? "Go-live moves to Nov 16" : "Scope review with Nam"}
            onChange={(event) => {
              set("title", event.target.value);
            }}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="hub-record-date">When</FieldLabel>
          <Input
            id="hub-record-date"
            type="date"
            value={draft.occurredOn}
            onChange={(event) => {
              set("occurredOn", event.target.value);
            }}
          />
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor="hub-record-body">
          {draft.type === "call" || draft.type === "meeting"
            ? "Notes or transcript"
            : draft.type === "email" || draft.type === "text"
              ? "Message"
              : "Details (optional)"}
        </FieldLabel>
        <Textarea
          id="hub-record-body"
          rows={draft.type === "decision" ? 2 : 6}
          maxLength={20_000}
          value={draft.bodyText}
          onChange={(event) => {
            set("bodyText", event.target.value);
          }}
        />
      </Field>
      <ComposerOptions draft={draft} set={set} canUpload={canUpload} decisions={decisions} />
    </>
  );
}

function ComposerOptions({
  draft,
  set,
  canUpload,
  decisions,
}: {
  draft: Draft;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  canUpload: boolean;
  decisions: readonly HubRecord[];
}) {
  const conversation = draft.type !== "note" && draft.type !== "decision";
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {conversation ? (
        <Field>
          <FieldLabel htmlFor="hub-record-direction">Direction</FieldLabel>
          <select
            id="hub-record-direction"
            className="customer-native-select"
            value={draft.direction}
            onChange={(event) => {
              set("direction", event.target.value as Direction);
            }}
          >
            <option value="inbound">From the customer</option>
            <option value="outbound">From us</option>
            <option value="internal">Internal</option>
          </select>
        </Field>
      ) : null}
      {draft.type === "decision" && decisions.length ? (
        <Field>
          <FieldLabel htmlFor="hub-record-replaces">Replaces</FieldLabel>
          <select
            id="hub-record-replaces"
            className="customer-native-select"
            value={draft.replaces}
            onChange={(event) => {
              set("replaces", event.target.value);
            }}
          >
            <option value="">Nothing</option>
            {decisions.map((record) => (
              <option key={record.id} value={record.id}>
                {record.title}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field>
        <FieldLabel htmlFor="hub-record-follow-up">Follow up by (optional)</FieldLabel>
        <Input
          id="hub-record-follow-up"
          type="date"
          value={draft.followUpOn}
          onChange={(event) => {
            set("followUpOn", event.target.value);
          }}
        />
      </Field>
      {canUpload && draft.type !== "decision" ? (
        <Field>
          <FieldLabel htmlFor="hub-record-file">Transcript or file (optional)</FieldLabel>
          <Input
            id="hub-record-file"
            type="file"
            onChange={(event) => {
              set("file", event.target.files?.[0] ?? null);
            }}
          />
        </Field>
      ) : null}
    </div>
  );
}
